import {
  ChangeFlagStageForm,
  ClientFeatureFlag,
  FeatureFlag,
  FeatureFlagKey,
  NewSystemFlagForm,
  RemoteClientFeatureFlag,
  RemoteFeatureFlag,
  RemoteUserFeatureFlag,
  UserFeatureFlag,
} from './featureFlags.types';
import _map from 'lodash/map';
import apiV2 from '../api/api-v2.service';
import {
  toUIClientFeatureFlag,
  toUIFeatureFlag,
  toUIUserFeatureFlag,
} from './featureFlags.dataConverters';
import toSetOfFlagKeys from './utils/toSetOfFlagKeys';
import {featureFlags} from '@cl/shared';
const {enabledFlagsPromise} = featureFlags;

const basePath = 'FeatureFlags';

async function getEnabledFlags({skipCache} = {skipCache: false}) {
  if (skipCache) enabledFlagsPromise.current = null;

  enabledFlagsPromise.current =
    enabledFlagsPromise.current ??
    apiV2
      .executeGetRequest<RemoteFeatureFlag[]>(`${basePath}/enabled`)
      .then((res) => toSetOfFlagKeys(res?.data?.map(toUIFeatureFlag)))
      .catch((e) => {
        enabledFlagsPromise.current = null;
        throw e;
      });

  return enabledFlagsPromise.current;
}

async function getAllSystemFlags() {
  const {data: flags} = await apiV2.executeGetRequest<RemoteFeatureFlag[]>(`${basePath}/allFlags`);

  return flags.map(toUIFeatureFlag);
}

async function getAllClientFlags() {
  const {data: flags} = await apiV2.executeGetRequest<RemoteClientFeatureFlag[]>(
    `${basePath}/allClientFlags`,
  );

  return flags.map(toUIClientFeatureFlag);
}

async function getAllUserFlags() {
  const {data: flags} = await apiV2.executeGetRequest<RemoteUserFeatureFlag[]>(
    `${basePath}/allUserFlags`,
  );

  return flags.map(toUIUserFeatureFlag);
}

async function setClientFlags({
  flags,
  clientIds,
  enable,
}: {
  flags: FeatureFlagKey[];
  clientIds: number[];
  enable: boolean;
}) {
  const promises = flags.flatMap((flagKey) =>
    clientIds.map((clientId) =>
      apiV2.executePutRequest<void>(`${basePath}/client/${clientId}`, null, {
        key: flagKey,
        value: enable,
      }),
    ),
  );

  await Promise.all(promises);
}

async function setUserFlags({
  flags,
  userIds,
  enable,
}: {
  flags: FeatureFlagKey[];
  userIds: number[];
  enable: boolean;
}) {
  const promises = flags.flatMap((flagKey) =>
    userIds.map((userId) =>
      apiV2.executePutRequest<void>(`${basePath}/user/${userId}`, null, {
        key: flagKey,
        value: enable,
      }),
    ),
  );

  await Promise.all(promises);
}

async function setSystemFlag(flag: FeatureFlag) {
  await apiV2.executePatchRequest<void>(basePath, null, {
    ...flag,
  });
}

async function setSystemFlags(flags: FeatureFlag[]) {
  const updateFlagPromises = flags.map(setSystemFlag);

  await Promise.all(updateFlagPromises);
}

async function setSystemFlagsStage(flagsData: ChangeFlagStageForm) {
  await apiV2.executePostRequest<void>(`${basePath}/SetAll`, null, {...flagsData});
}

async function deleteSystemFlags(flagsToDelete: FeatureFlag[]) {
  const {data: deletedFlags} = await apiV2.executeDeleteRequest<FeatureFlagKey[]>(
    `${basePath}`,
    null,
    {
      keys: _map(flagsToDelete, 'key'),
    },
  );

  return deletedFlags;
}

async function deleteClientFlags(flagsToDelete: ClientFeatureFlag[]) {
  const deleteFlagPromises = flagsToDelete.map(({key, clientId}) =>
    apiV2.executeDeleteRequest<void>(`${basePath}/client/${clientId}`, null, {key}),
  );

  await Promise.all(deleteFlagPromises);
}

async function deleteUserFlags(flagsToDelete: UserFeatureFlag[]) {
  const deleteFlagPromises = flagsToDelete.map(({key, userId}) =>
    apiV2.executeDeleteRequest<void>(`${basePath}/user/${userId}`, null, {key}),
  );

  await Promise.all(deleteFlagPromises);
}

async function createSystemFlag(newFlag: NewSystemFlagForm) {
  await apiV2.executePutRequest<void>(`${basePath}`, null, {...newFlag});
}

async function createSystemFlags(newFlags: NewSystemFlagForm[]) {
  const createFlagPromises = newFlags.map(createSystemFlag);

  await Promise.all(createFlagPromises);
}

async function getEnabledFlagsForSpecificClient(clientId: number) {
  const {data} = await apiV2.executeGetRequest<RemoteFeatureFlag[]>(
    `${clientId}/${basePath}/enabled`,
    {},
    {includeClientId: false},
  );

  return new Set(_map(data, 'key'));
}

const featureFlagsApi = {
  getEnabledFlags,
  getAllSystemFlags,
  getAllClientFlags,
  getAllUserFlags,
  setClientFlags,
  setSystemFlag,
  setSystemFlagsStage,
  setUserFlags,
  createSystemFlag,
  deleteSystemFlags,
  deleteClientFlags,
  deleteUserFlags,
  createSystemFlags,
  setSystemFlags,
  getEnabledFlagsForSpecificClient,
};

export default featureFlagsApi;
